import React, { forwardRef, useId, useState, useEffect } from 'react'
import classNames from 'classnames'
import { getInputHelperTextClasses } from '../utils/inputStyles'

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  label?: string
  error?: boolean
  helperText?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, helperText, className = '', disabled, id, checked, onChange, name, defaultChecked, ...props }, ref) => {
    const autoId = useId()
    const checkboxId = id || `checkbox-${autoId}`
    // Estado interno para modo não controlado
    // Inicializar com defaultChecked se fornecido, senão false
    const [internalChecked, setInternalChecked] = useState(() => {
      if (defaultChecked !== undefined) return defaultChecked
      return false
    })
    
    // Determinar se está em modo controlado ou não controlado
    const isControlled = checked !== undefined
    const currentChecked = isControlled ? checked : internalChecked
    
    // Atualizar estado interno quando defaultChecked mudar (apenas em modo não controlado)
    useEffect(() => {
      if (!isControlled && defaultChecked !== undefined) {
        setInternalChecked(defaultChecked)
      }
    }, [defaultChecked, isControlled])

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = event.target.checked
      if (!isControlled) {
        setInternalChecked(newChecked)
      }
      if (onChange) {
        onChange(event)
      }
    }

    const checkboxBoxClasses = classNames(
      // Base styles
      'relative flex items-center justify-center w-4 h-4 rounded border-2 transition-all duration-200',
      'cursor-pointer flex-shrink-0',
      
      // Estados normais
      {
        'bg-white border-gray-300': !currentChecked && !error && !disabled,
        'bg-secondary-900 border-secondary-900': currentChecked && !error && !disabled,
        'bg-red-50 border-red-300': !currentChecked && error && !disabled,
        'bg-red-100 border-red-500': currentChecked && error && !disabled,
      },
      
      // Estados desabilitados
      {
        'bg-gray-100 border-gray-300 cursor-not-allowed opacity-50': disabled,
      },
      
      // Hover (apenas quando não está desabilitado)
      !disabled && {
        'hover:border-secondary-800': !currentChecked && !error,
        'hover:border-red-400': !currentChecked && error,
      },
      
      // Focus ring - usando cores do design system
      'focus-within:ring-4 focus-within:ring-secondary-500/10 focus-within:outline-none',
      error && 'focus-within:ring-red-500/10'
    )

    const inputClasses = classNames(
      'absolute inset-0 w-full h-full opacity-0 cursor-pointer',
      disabled && 'cursor-not-allowed'
    )

    const labelClasses = classNames(
      'text-sm font-medium leading-5',
      {
        'text-gray-700': !error,
        'text-red-700': error,
        'opacity-50 cursor-not-allowed': disabled,
        'cursor-pointer': !disabled,
      }
    )

    // Se não tiver label, renderiza apenas a caixa (para uso externo com label customizado)
    if (!label) {
      return (
        <div className={classNames('inline-flex', className)}>
          <div className={checkboxBoxClasses}>
            <input
              ref={ref}
              id={checkboxId}
              type="checkbox"
              checked={isControlled ? checked : undefined}
              defaultChecked={!isControlled ? defaultChecked : undefined}
              onChange={handleChange}
              disabled={disabled}
              name={name}
              className={inputClasses}
              aria-invalid={error}
              aria-describedby={helperText ? `${checkboxId}-helper` : undefined}
              {...props}
            />
            {currentChecked && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={classNames(
                  'absolute pointer-events-none',
                  error ? 'text-red-600' : 'text-white'
                )}
                aria-hidden="true"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
          </div>
          {helperText && (
            <p 
              id={`${checkboxId}-helper`}
              className={getInputHelperTextClasses(error)}
            >
              {helperText}
            </p>
          )}
        </div>
      )
    }

    return (
      <div className={classNames('w-full', className)}>
        <div className="flex items-center gap-2">
          <div className={checkboxBoxClasses}>
            <input
              ref={ref}
              id={checkboxId}
              type="checkbox"
              checked={isControlled ? checked : undefined}
              defaultChecked={!isControlled ? defaultChecked : undefined}
              onChange={handleChange}
              disabled={disabled}
              name={name}
              className={inputClasses}
              aria-invalid={error}
              aria-describedby={helperText ? `${checkboxId}-helper` : undefined}
              {...props}
            />
            {currentChecked && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={classNames(
                  'absolute pointer-events-none',
                  error ? 'text-red-600' : 'text-white'
                )}
                aria-hidden="true"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
          </div>
          <label
            htmlFor={checkboxId}
            className={labelClasses}
          >
            {label}
          </label>
        </div>
        {helperText && (
          <p 
            id={`${checkboxId}-helper`}
            className={getInputHelperTextClasses(error)}
          >
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Checkbox.displayName = 'Checkbox'

export default Checkbox

